import { useEffect, useState, type FormEvent } from 'react';

import { PageChrome } from './PageChrome';

type ContactFormState = {
  name: string;
  email: string;
  message: string;
  website: string;
};

type ApiResponse = {
  success?: boolean;
  message?: string;
  error?: string;
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const initialForm: ContactFormState = {
  name: '',
  email: '',
  message: '',
  website: ''
};

export function ContactPage() {
  const [form, setForm] = useState<ContactFormState>(initialForm);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const metaDescription = window.document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const originalTitle = window.document.title;
    const originalDescription = metaDescription?.getAttribute('content') ?? '';

    window.document.title = 'Kapcsolat | Orienta';
    metaDescription?.setAttribute(
      'content',
      'Kapcsolatfelvétel az Orienta csapatával, ügyfélszolgálati, adatvédelmi és panaszkezelési elérhetőségek.'
    );

    return () => {
      window.document.title = originalTitle;
      metaDescription?.setAttribute('content', originalDescription);
    };
  }, []);

  function updateField(field: keyof ContactFormState, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function validate() {
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();

    if (!name || !email || !message) return 'Minden mező kitöltése kötelező.';
    if (name.length > 100) return 'A név 1-100 karakter hosszú legyen.';
    if (!emailPattern.test(email)) return 'Érvénytelen email cím.';
    if (message.length < 10 || message.length > 5000) return 'Az üzenet 10-5000 karakter hosszú legyen.';
    return '';
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSending(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          website: form.website
        })
      });
      const body = (await response.json()) as ApiResponse;

      if (!response.ok || !body.success) {
        setError(body.error ?? 'Nem sikerült elküldeni. Kérjük, próbáld újra később.');
        return;
      }

      setIsSent(true);
      setForm(initialForm);
    } catch {
      setError('Hálózati hiba. Kérjük, ellenőrizd az internetkapcsolatod.');
    } finally {
      setIsSending(false);
    }
  }

  return (
    <PageChrome>
      <main className="form-page-main">
        <section className="section-inner form-page-layout">
          <div className="form-page-copy">
            <p className="eyebrow">Kapcsolat</p>
            <h1>Lépj kapcsolatba az Orienta csapatával</h1>
            <p>
              Kérdés, béta hozzáférés, adatvédelmi ügy vagy panasz esetén az alábbi csatornákon érhető el a
              Szolgáltató.
            </p>

            <div className="contact-grid" aria-label="Elérhetőségek">
              <ContactItem label="Általános kapcsolat" href="mailto:info@orientaplatform.eu" value="info@orientaplatform.eu" />
              <ContactItem label="Ügyfélszolgálat" href="mailto:segitseg@orientaplatform.eu" value="segitseg@orientaplatform.eu" />
              <ContactItem label="Adatvédelem" href="mailto:adatvedelem@orientaplatform.eu" value="adatvedelem@orientaplatform.eu" />
              <ContactItem label="Panaszkezelés" href="mailto:panasz@orientaplatform.eu" value="panasz@orientaplatform.eu" />
              <ContactItem label="DSA kapcsolattartás" href="mailto:dsa@orientaplatform.eu" value="dsa@orientaplatform.eu" />
              <ContactItem label="Telefon" href="tel:+36706072310" value="+36 70 607 2310" />
            </div>

            <address>
              Extra SWEET Solutions Kereskedelmi és Szolgáltató Korlátolt Felelősségű Társaság
              <br />
              2013 Pomáz, Rozmaring utca 5.
            </address>
          </div>

          <section className="form-panel" aria-labelledby="contact-form-title">
            {isSent ? (
              <div className="success-panel" role="status">
                <span className="success-mark">
                  <CheckIcon />
                </span>
                <h2 id="contact-form-title">Köszönjük!</h2>
                <p>Az üzenetet megkaptuk, és szükség esetén felvesszük veled a kapcsolatot.</p>
                <a href="/" className="secondary-button">
                  Vissza a főoldalra
                </a>
              </div>
            ) : (
              <>
                <h2 id="contact-form-title">Üzenet küldése</h2>
                <form onSubmit={handleSubmit} noValidate>
                  <label>
                    Név
                    <input
                      value={form.name}
                      onChange={(event) => updateField('name', event.target.value)}
                      placeholder="Teljes neved"
                      autoComplete="name"
                      disabled={isSending}
                    />
                  </label>

                  <label>
                    Email
                    <input
                      value={form.email}
                      onChange={(event) => updateField('email', event.target.value)}
                      placeholder="email@cim.hu"
                      type="email"
                      autoComplete="email"
                      disabled={isSending}
                    />
                  </label>

                  <label>
                    Üzenet
                    <textarea
                      value={form.message}
                      onChange={(event) => updateField('message', event.target.value)}
                      placeholder="Miben segíthetünk?"
                      disabled={isSending}
                    />
                  </label>

                  <label className="honeypot-field">
                    Website
                    <input
                      value={form.website}
                      onChange={(event) => updateField('website', event.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>

                  {error ? (
                    <p className="form-error" role="alert">
                      {error}
                    </p>
                  ) : null}

                  <button className="submit-button" type="submit" disabled={isSending}>
                    {isSending ? 'Küldés folyamatban…' : 'Üzenet küldése'}
                    <ArrowIcon />
                  </button>
                </form>
              </>
            )}
          </section>
        </section>
      </main>
    </PageChrome>
  );
}

function ContactItem({ label, href, value }: { label: string; href: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <a href={href}>{value}</a>
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h9M9 5l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 10.5l3.5 3.5L17 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
